import { useState } from "react";
import { IconPlus } from "@tabler/icons-react";
import {
  Button,
  Card,
  Checkbox,
  Group,
  Heading,
  NumberInput,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  Stack,
  Text,
} from "@/components/ui";
import { cn } from "@/lib/cn";
import { toastError } from "@/lib/toast";
import type { Area } from "@/lib/types";
import { TIPOS_SOLICITUD, subtiposDe } from "../data/tipos-subtipos";
import { useRolesAsignables } from "../hooks/use-roles-asignables";
import { useCrearNivelEnAreas } from "../hooks/use-niveles-mutations";

/**
 * Form de creación · acepta el catálogo de áreas como prop (lo pasa el
 * padre, ya cargado vía loader). Esto desacopla el form de la fuente
 * del listado · facilita tests.
 */
export function NivelCrearForm({ areas, onSalir }: { areas: Area[]; onSalir: () => void }) {
  const [tipo, setTipo] = useState("FB60");
  const [subtipo, setSubtipo] = useState("CFDI_UNICO");

  // Al cambiar el tipo, reajusta el subtipo al primero válido de ese tipo
  // (evita dejar una combinación tipo/subtipo inexistente).
  const cambiarTipo = (nuevoTipo: string) => {
    setTipo(nuevoTipo);
    const validos = subtiposDe(nuevoTipo);
    if (!validos.some((s) => s.value === subtipo)) {
      setSubtipo(validos[0]?.value ?? "");
    }
  };
  // Múltiples áreas · se crea un nivel idéntico en cada una.
  const [areaIds, setAreaIds] = useState<string[]>(areas[0]?.id ? [areas[0].id] : []);
  const [nivel, setNivel] = useState<number | "">(1);
  const [rolesSel, setRolesSel] = useState<Set<string>>(new Set(["AutorizadorNivel1"]));
  const [generaPoliza, setGeneraPoliza] = useState(false);

  const rolesAsignables = useRolesAsignables();
  const crear = useCrearNivelEnAreas({ onCreado: onSalir });

  const submit = () => {
    if (!tipo.trim() || !subtipo.trim() || areaIds.length === 0) {
      toastError({ title: "Datos incompletos", message: "Tipo, subtipo y al menos un área son obligatorios." });
      return;
    }
    const n = Number(nivel);
    if (!n || n < 1) {
      toastError({ title: "Nivel inválido", message: "Nivel debe ser >= 1." });
      return;
    }
    if (rolesSel.size === 0) {
      toastError({ title: "Sin roles", message: "Selecciona al menos un rol autorizador." });
      return;
    }
    crear.mutate({
      tipo: tipo.trim(),
      subtipo: subtipo.trim(),
      areaIds,
      nivel: n,
      rolesAutorizadores: Array.from(rolesSel),
      generaPolizaAlAprobar: generaPoliza,
    });
  };

  const toggleRol = (r: string) => {
    const next = new Set(rolesSel);
    if (next.has(r)) next.delete(r);
    else next.add(r);
    setRolesSel(next);
  };

  return (
    <Card padding="lg">
      <Stack gap="md">
        <div>
          <Heading order={5}>Nuevo nivel de autorización</Heading>
          <Text size="sm" c="dimmed" className="mt-0.5">
            Selecciona una o más áreas · se creará el mismo nivel en cada una de las áreas elegidas (la combinación
            tipo, subtipo, área, nivel debe ser única).
          </Text>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <Select
            label="Tipo"
            options={TIPOS_SOLICITUD.map((t) => ({ value: t.value, label: t.label }))}
            value={tipo}
            onValueChange={cambiarTipo}
          />
          <Select label="Subtipo" options={subtiposDe(tipo)} value={subtipo} onValueChange={setSubtipo} />
          <AreasMultiSelect areas={areas} value={areaIds} onChange={setAreaIds} />
          <NumberInput label="Nivel" min={1} value={nivel} onChange={setNivel} />
        </div>
        <div>
          <Text size="xs" c="dimmed" fw={600} className="mb-2 uppercase tracking-wide">
            Roles autorizadores
          </Text>
          <Group gap="md" className="flex-wrap">
            {rolesAsignables.map((r) => (
              <Checkbox key={r} label={r} checked={rolesSel.has(r)} onCheckedChange={() => toggleRol(r)} />
            ))}
          </Group>
        </div>
        <div>
          <Text size="xs" c="dimmed" fw={600} className="mb-2 uppercase tracking-wide">
            Generación de póliza
          </Text>
          <Checkbox
            label="Generar la póliza TXT al aprobar este nivel"
            checked={generaPoliza}
            onCheckedChange={(v) => setGeneraPoliza(Boolean(v))}
          />
          <Text size="xs" c="dimmed" className="mt-1">
            Por defecto la póliza se genera al enviar la solicitud a autorización. Márcalo para
            diferir la generación hasta que este nivel apruebe (p. ej. Invoice de Contrato Anualizado /
            Orden de Compra aprobado por Contabilidad).
          </Text>
        </div>
        <Group justify="flex-end">
          <Button variant="default" onClick={onSalir}>
            Cancelar
          </Button>
          <Button leftSection={<IconPlus size={14} />} loading={crear.isPending} onClick={submit}>
            {areaIds.length > 1 ? `Crear ${areaIds.length} niveles` : "Crear nivel"}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

/**
 * Selector de múltiples áreas · reemplazo bespoke de `<MultiSelect>` de
 * Mantine (no hay átomo compartido para esto todavía · único consumidor).
 * Popover con checkboxes + trigger que muestra los chips seleccionados.
 */
function AreasMultiSelect({
  areas,
  value,
  onChange,
}: {
  areas: Area[];
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const todasSeleccionadas = value.length === areas.length && areas.length > 0;
  const toggleTodas = () => onChange(todasSeleccionadas ? [] : areas.map((a) => a.id));
  const toggleUna = (id: string) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const seleccionadas = areas.filter((a) => value.includes(a.id));

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-foreground">Áreas</label>
        <button type="button" className="cursor-pointer text-xs font-medium text-multiva-700 hover:underline" onClick={toggleTodas}>
          {todasSeleccionadas ? "Limpiar" : "Todas"}
        </button>
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex min-h-8 w-full flex-wrap items-center gap-1 rounded-md border border-input bg-background px-2 py-1 text-left text-sm transition-[border-color,box-shadow]",
              "focus:outline-none focus:ring-2 focus:ring-multiva-600/30 focus:border-multiva-600"
            )}
          >
            {seleccionadas.length === 0 ? (
              <span className="text-muted-foreground">Selecciona al menos un área</span>
            ) : (
              seleccionadas.map((a) => (
                <span key={a.id} className="rounded-sm bg-muted px-1.5 py-0.5 text-xs">
                  {a.nombre}
                </span>
              ))
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="max-h-60 w-72 overflow-y-auto p-2">
          <Stack gap={4}>
            {areas.map((a) => (
              <Checkbox key={a.id} label={a.nombre} checked={value.includes(a.id)} onCheckedChange={() => toggleUna(a.id)} />
            ))}
          </Stack>
        </PopoverContent>
      </Popover>
    </div>
  );
}
